import { ExerciseMetricDisplayMetadata, SortType as StoredSortType } from "./output-exercise";

/**
 * @description
 * How a {@link CircuitMetric} aggregates its round values.
 *
 * The API enum also defines `Worst`, which is reserved/unused on the FE.
 */
export enum CircuitAggregationType {
  Sum = "Sum",
  Average = "Average",
  Best = "Best",
}

/**
 * @description
 * Metric sorting supplied on circuit create requests (`CircuitAdd.metricSorting`).
 *
 * NB: this is distinct from the stored `sortType` on a {@link CircuitMetric} /
 * {@link RoundMetric}, which is an `int?` (0 = lower better, 1 = higher better,
 * null = neutral) — see {@link StoredSortType}.
 */
export enum SortType {
  LowerBetter = "LowerBetter",
  HigherBetter = "HigherBetter",
}

/** @description The category/type of a circuit. */
export enum CircuitCategory {
  Conditioning = "Conditioning",
  WarmUp = "WarmUp",
  Cooldown = "Cooldown",
  Rehab = "Rehab",
  Challenge = "Challenge",
  SpeedAgility = "SpeedAgility",
}

/**
 * @description
 * The per-round input a circuit measures, supplied on create (`CircuitAdd.input`).
 *
 * The server uses this (with `aggregation` and `metricSorting`) to generate the
 * circuit's {@link CircuitMetric}s and {@link RoundMetric}. `None` produces a
 * round-count-only circuit. NB: the API enum also defines `Rounds` (dropped from
 * the UI) and reserves `Custom` (deferred) — neither is surfaced here.
 */
export enum CircuitInputType {
  None = "None",
  Reps = "Reps",
  Duration = "Duration",
  DistanceMetres = "DistanceMetres",
  DistanceYards = "DistanceYards",
}

/**
 * @description
 * A single per-round input metric (e.g. Reps, Duration, Distance). Mirrors an
 * exercise rep metric. Empty for a circuit with no input.
 */
export type RoundMetric = {
  /** Measurement field written per round, e.g. `repCount` | `duration` | `distance`. */
  field: string;
  /** Display/input name, e.g. "Reps", "Duration". */
  name: string;
  /** 0 = lower better, 1 = higher better, null = neutral. */
  sortType: StoredSortType | null;
  storageUnit: string;
  displayMetadata: ExerciseMetricDisplayMetadata;
};

/**
 * @description
 * A circuit-level metric, generated server-side from (input × aggregation × sorting).
 * Mirrors an exercise metric with circuit-specific additions. Every circuit has a
 * `roundCount` metric; circuits with an input also expose aggregated input metrics.
 */
export type CircuitMetric = {
  /** Named aggregate key (e.g. "totalDistance") or "roundCount". Also the localisation key for the display name. */
  field: string;
  /** Display name, e.g. "Total Duration", "Rounds Completed". */
  name: string;
  /** Exactly one circuit metric is the default. */
  isDefault: boolean;
  /** True when `sortType` is not null. */
  isPbEnabled: boolean;
  /** 0 = lower better, 1 = higher better, null = neutral. */
  sortType: StoredSortType | null;
  /** How the round field is aggregated; null for the `roundCount` metric. */
  aggregationType: CircuitAggregationType | null;
  /** The {@link RoundMetric} `field` being aggregated; null for the `roundCount` metric. */
  aggregationRoundField: string | null;
  storageUnit: string;
  displayMetadata: ExerciseMetricDisplayMetadata;
};

/**
 * @description
 * A circuit, matching the live `CircuitView` contract (`GET /api/v1/circuits`).
 * Circuits are discriminated from exercises on measurements via
 * `Measurement.exerciseType === ExerciseType.Circuit`, not by a field on this type.
 */
export interface Circuit {
  id: string;
  name: string;
  categories: CircuitCategory[];
  instructions: string;
  /** Default number of rounds (1–20); editable per workout instance. */
  roundCount: number;
  metrics: CircuitMetric[];
  /** Single round metric, or empty for a circuit with no input. */
  roundMetrics: RoundMetric[];
  videoId: string | null;
  creatorId: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * @description
 * Create payload for `POST /api/v1/circuits` (`CircuitAdd`). Sends only the
 * high-level inputs — the server generates `metrics[]`/`roundMetrics[]` from
 * `input` × `aggregation` × `metricSorting`, so do NOT build metric arrays here.
 */
export interface CircuitAdd {
  name: string;
  instructions: string;
  videoId: string | null;
  /** Default number of rounds (1–20). */
  roundCount: number;
  input: CircuitInputType;
  /** How the input is scored across rounds; `null` for a `None`-input (completion-only) circuit. */
  aggregation: CircuitAggregationType | null;
  /**
   * Ranking direction of the generated metric. `null` = Neutral (PB disabled), and is
   * also sent for a `None`-input circuit. `LowerBetter`/`HigherBetter` set the PB direction.
   */
  metricSorting: SortType | null;
  categories: CircuitCategory[];
}

/**
 * @description
 * Update payload for `PUT /api/v1/circuits/{id}` (`CircuitEdit`). The circuit's
 * input/aggregation/sorting (and therefore its generated metrics) are immutable
 * after creation, so only the descriptive fields and `roundCount` are editable.
 */
export type CircuitEdit = Omit<CircuitAdd, "input" | "aggregation" | "metricSorting">;
